import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Camera, User, MapPin, Calendar, Sparkles, X, Check, ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Recommended image dimensions
const IMAGE_DIMENSIONS = {
  avatar: { width: 320, height: 320 },      // Profile photo (displays as circle)
  cover: { width: 820, height: 312 }        // Cover photo (landscape)
};

// Image resizing utility
const resizeImage = (file, maxWidth, maxHeight, type = 'avatar') => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      let { width, height } = img;
      
      if (type === 'avatar') {
        // For avatar: crop to square from center, then resize
        const size = Math.min(width, height);
        const sx = (width - size) / 2;
        const sy = (height - size) / 2;
        
        canvas.width = maxWidth;
        canvas.height = maxHeight;
        ctx.drawImage(img, sx, sy, size, size, 0, 0, maxWidth, maxHeight);
      } else {
        // For cover: maintain aspect ratio, crop to fit
        const targetRatio = maxWidth / maxHeight;
        const imgRatio = width / height;
        
        let sx = 0, sy = 0, sw = width, sh = height;
        
        if (imgRatio > targetRatio) {
          // Image is wider - crop sides
          sw = height * targetRatio;
          sx = (width - sw) / 2;
        } else {
          // Image is taller - crop top/bottom
          sh = width / targetRatio;
          sy = (height - sh) / 2;
        }
        
        canvas.width = maxWidth;
        canvas.height = maxHeight;
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, maxWidth, maxHeight);
      }
      
      // Convert to base64 with compression
      const quality = 0.85;
      const base64 = canvas.toDataURL('image/jpeg', quality);
      resolve(base64);
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

export default function EditProfilePage({ user, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [showAvatarPreview, setShowAvatarPreview] = useState(false);
  const [showCoverPreview, setShowCoverPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    username: '',
    bio: '',
    location: '',
    dob: '',
    gender: '',
    avatar: '',
    cover_photo: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/user/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const profile = response.data;
      setFormData({
        name: profile.name || '',
        username: profile.username || '',
        bio: profile.bio || '',
        location: profile.location || '',
        dob: profile.dob || '',
        gender: profile.gender || '',
        avatar: profile.avatar || '',
        cover_photo: profile.cover_photo || ''
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = async (file, type) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (JPG, PNG, etc.)');
      return;
    }

    // Validate file size (10MB max for original)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    const setUploading = type === 'avatar' ? setUploadingAvatar : setUploadingCover;
    setUploading(true);

    try {
      const dimensions = type === 'avatar' ? IMAGE_DIMENSIONS.avatar : IMAGE_DIMENSIONS.cover;
      const resizedBase64 = await resizeImage(file, dimensions.width, dimensions.height, type);
      
      // Show preview
      setPreviewImage(resizedBase64);
      setPreviewType(type);
      setUploading(false);
    } catch (error) {
      toast.error('Failed to process image');
      setUploading(false);
    }
  };

  const confirmImageUpload = () => {
    if (previewType === 'avatar') {
      setFormData(prev => ({ ...prev, avatar: previewImage }));
      toast.success('Profile photo updated!');
    } else {
      setFormData(prev => ({ ...prev, cover_photo: previewImage }));
      toast.success('Cover photo updated!');
    }
    setPreviewImage(null);
    setPreviewType(null);
  };

  const cancelImageUpload = () => {
    setPreviewImage(null);
    setPreviewType(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.put(`${API}/user/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local user state
      if (setUser && response.data.user) {
        const updatedUser = response.data.user;
        setUser(updatedUser);
        localStorage.setItem('dharmbook_user', JSON.stringify(updatedUser));
      }
      
      toast.success('Profile updated successfully!');
      navigate('/profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-2">ॐ</div>
          <p className="text-slate-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto px-2 sm:px-4 py-4 pb-20 md:pb-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-200">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Edit Profile</h1>
        </div>

        {/* Cover Photo */}
        <div className="relative h-36 sm:h-48 bg-gradient-to-br from-orange-100 to-amber-50 overflow-hidden">
          {formData.cover_photo ? (
            <img
              src={formData.cover_photo}
              alt="Cover"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Sparkles className="w-12 h-12 text-orange-200" />
            </div>
          )}
          <button
            onClick={() => coverInputRef.current?.click()}
            disabled={uploadingCover}
            className="absolute bottom-3 right-3 flex items-center gap-2 px-3 py-2 bg-black/50 hover:bg-black/70 text-white text-sm rounded-lg transition-colors"
            data-testid="change-cover-btn"
          >
            <Camera className="w-4 h-4" />
            {uploadingCover ? 'Uploading...' : 'Change Cover'}
          </button>
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleImageSelect(e.target.files?.[0], 'cover')}
          />
        </div>

        {/* Avatar */}
        <div className="px-4 -mt-16 relative z-10">
          <div className="relative inline-block">
            <div className="w-28 h-28 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gray-200">
              {formData.avatar ? (
                <img
                  src={formData.avatar}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-orange-100">
                  <User className="w-12 h-12 text-orange-300" />
                </div>
              )}
            </div>
            <button
              onClick={() => avatarInputRef.current?.click()}
              disabled={uploadingAvatar}
              className="absolute bottom-0 right-0 w-9 h-9 bg-[#FF9933] hover:bg-[#E68A00] rounded-full flex items-center justify-center shadow-lg transition-colors"
              data-testid="change-avatar-btn"
            >
              <Camera className="w-4 h-4 text-white" />
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleImageSelect(e.target.files?.[0], 'avatar')}
            />
          </div>
        </div>

        {/* Image Size Info */}
        <div className="px-4 pt-4">
          <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <ImageIcon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-700">
              <p className="font-medium">Recommended photo dimensions:</p>
              <p>Profile Photo: 320×320px (auto-cropped to square)</p>
              <p>Cover Photo: 820×312px (auto-cropped to fit)</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="p-4 pt-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your full name"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 focus:outline-none transition-all"
              data-testid="name-input"
            />
          </div>

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Username
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">@</span>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, '') })}
                placeholder="username"
                className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 focus:outline-none transition-all"
                data-testid="username-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Only letters, numbers, dots and underscores</p>
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Bio
            </label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Tell us about your spiritual journey..."
              rows={3}
              maxLength={200}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 focus:outline-none transition-all resize-none"
              data-testid="bio-input"
            />
            <p className="text-xs text-gray-500 mt-1 text-right">{formData.bio.length}/200</p>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Location
            </label>
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="City, State"
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 focus:outline-none transition-all"
                data-testid="location-input"
              />
            </div>
          </div>

          {/* DOB and Gender */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Date of Birth
              </label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={formData.dob}
                  onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 focus:outline-none transition-all"
                  data-testid="dob-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Gender
              </label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 focus:outline-none transition-all appearance-none bg-white"
                data-testid="gender-select"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
            </div>
          </div>

          {/* Info Note */}
          <div className="p-4 bg-orange-50 rounded-xl border border-orange-100">
            <p className="text-sm text-orange-800">
              <strong>Note:</strong> To change your email or mobile number, please go to{' '}
              <button 
                onClick={() => navigate('/settings')} 
                className="text-[#FF9933] font-medium underline"
              >
                Account Settings
              </button>
              {' '}(requires OTP verification).
            </p>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3.5 bg-[#FF9933] hover:bg-[#E68A00] text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            data-testid="save-profile-btn"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={cancelImageUpload}>
          <div 
            className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {previewType === 'avatar' ? 'Profile Photo Preview' : 'Cover Photo Preview'}
              </h3>
              <button
                onClick={cancelImageUpload}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Preview Image */}
            <div className="p-4 bg-gray-50">
              {previewType === 'avatar' ? (
                <div className="flex flex-col items-center gap-4">
                  <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-white shadow-lg">
                    <img
                      src={previewImage}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-sm text-gray-500">320×320 pixels • Square crop</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <div className="w-full rounded-lg overflow-hidden shadow-lg" style={{ aspectRatio: '820/312' }}>
                    <img
                      src={previewImage}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-sm text-gray-500">820×312 pixels • Cover photo</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-4 border-t border-gray-200">
              <button
                onClick={cancelImageUpload}
                className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmImageUpload}
                className="flex-1 py-3 px-4 bg-[#FF9933] hover:bg-[#E68A00] text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                data-testid="confirm-image-btn"
              >
                <Check className="w-5 h-5" />
                Use This Photo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
